import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios, { getToken, setToken } from '../utils/axiosInstance';

const AuthContext = createContext();

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
};

/**
 * Passwordless email one-time-code auth.
 *  requestCode(email)        -> POST /api/auth/request  (emails / dev-returns a code)
 *  verifyCode(email, code)   -> POST /api/auth/verify   (stores the session JWT)
 * Auth state is simply "do we hold a valid token".
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadProfile = useCallback(async () => {
    if (!getToken()) { setUser(null); setLoading(false); return; }
    try {
      const { data } = await axios.get('api/auth/profile/');
      setUser(data.user || data);
    } catch {
      // token invalid/expired -> sign out
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadProfile(); }, [loadProfile]);

  const requestCode = useCallback(async (email) => {
    const { data } = await axios.post('api/auth/request/', { email });
    return data; // { challenge, sent, dev_code? }
  }, []);

  const verifyCode = useCallback(async (email, code, challenge) => {
    const { data } = await axios.post('api/auth/verify/', { email, code, challenge });
    setToken(data.token);
    setUser(data.user);
    return data;
  }, []);

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{
      user, loading, isAuthenticated: !!user,
      requestCode, verifyCode, logout,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
